import { openDB, type IDBPDatabase } from 'idb';

/**
 * Profilregister: vilken lokal profil som är aktiv på enheten. Varje profil har en EGEN lokal databas.
 *  - Gästprofil ("Endast på den här enheten").
 *  - En kontoprofil per (serverinstans, användar-ID) – aldrig per visningsnamn eller e-post.
 * Registret innehåller inga lösenord och inga sessionstoken.
 * Migreringen från en äldre version beskrivs i docs/ACCOUNT-UX.md och är icke-destruktiv.
 */
export const REGISTRY_DB = 'autoday-profiles';
export const LEGACY_DB = 'autoday';
export const GUEST_ID = 'guest';
const KEY = 'registry';
const CHANNEL = 'autoday-profiles';

export interface ProfileMeta {
  id: string;
  kind: 'guest' | 'account';
  /** Namn på profilens lokala databas (IndexedDB/SQLite). */
  dbName: string;
  createdAt: string;
  /** Konto: '' = samma ursprung som appen, annars uttryckligen vald server (Egen server / avancerat). */
  server?: string;
  /** Konto: serverns absoluta ursprung när profilen skapades (visning och profilgräns). */
  serverOrigin?: string;
  /** Konto: användar-ID från servern. null = äldre profil som ännu inte bekräftats. */
  userId?: string | null;
  email?: string;
  name?: string;
  /** Opak notismarkering (accountTagFor(userId)). */
  accountTag?: string | null;
  rememberMe?: boolean;
  lastVerifiedAt?: string | null;
  /** Utloggad på den här enheten. Den lokala kopian finns kvar för nästa inloggning. */
  signedOut?: boolean;
  /** Migrerad från en version före profilerna (data låg i 'autoday'). */
  legacy?: boolean;
}

/** Utloggning som servern ännu inte bekräftat (t.ex. utloggning offline). */
export interface PendingRevocation {
  profileId: string;
  userId: string | null;
  server: string;
  deviceId: string | null;
  since: string;
}

export interface Registry {
  version: 1;
  activeProfileId: string;
  profiles: Record<string, ProfileMeta>;
  pendingRevocations: PendingRevocation[];
}

export interface LegacyProbe {
  /** Finns ett anslutet konto i den äldre databasen? */
  account: { serverUrl: string; email: string } | null;
}

let dbp: Promise<IDBPDatabase> | null = null;
function db(factory?: IDBFactory): Promise<IDBPDatabase> {
  if (dbp && !factory) return dbp;
  const open = () =>
    openDB(REGISTRY_DB, 1, {
      upgrade(d) {
        if (!d.objectStoreNames.contains('kv')) d.createObjectStore('kv');
      },
    });
  if (factory) {
    const g = globalThis as { indexedDB?: IDBFactory };
    const prev = g.indexedDB;
    g.indexedDB = factory;
    const p = open();
    g.indexedDB = prev;
    dbp = p;
    return p;
  }
  dbp = open();
  return dbp;
}

/**
 * Enhetens presentationsval som service workern behöver (den når inte localStorage). Bara språk – inga
 * kalenderdata. Skrivs efter att registret öppnats (påverkar aldrig "befintlig installation"-kontrollen).
 */
export async function writeUiPrefs(prefs: { lang: 'sv' | 'en' }, factory?: IDBFactory): Promise<void> {
  await (await db(factory)).put('kv', { lang: prefs.lang }, 'ui');
}

/** För tester: glöm öppen anslutning. */
export function resetRegistryConnection() {
  dbp = null;
}

export async function readRegistry(factory?: IDBFactory): Promise<Registry | null> {
  return ((await (await db(factory)).get('kv', KEY)) as Registry | undefined) ?? null;
}

export async function writeRegistry(r: Registry, factory?: IDBFactory): Promise<void> {
  await (await db(factory)).put('kv', r, KEY);
}

/** Läs–ändra–skriv i en transaktion så att två flikar inte skriver över varandras ändringar. */
export async function updateRegistry(fn: (r: Registry) => Registry, factory?: IDBFactory): Promise<Registry> {
  const d = await db(factory);
  const tx = d.transaction('kv', 'readwrite');
  const cur = (await tx.store.get(KEY)) as Registry | undefined;
  if (!cur) {
    tx.abort();
    throw new Error('Profilregistret saknas.');
  }
  const next = fn(structuredClone(cur));
  await tx.store.put(next, KEY);
  await tx.done;
  return next;
}

function nowIso() {
  return new Date().toISOString();
}

/**
 * Öppnar registret och skapar det första gången. Äldre data i 'autoday' flyttas inte:
 *  - utan anslutet konto blir den gästprofilens databas;
 *  - med anslutet konto blir den en kontoprofil ("legacy") och gästprofilen får en ny tom databas.
 */
export async function openRegistry(probeLegacy: () => Promise<LegacyProbe | null>, factory?: IDBFactory): Promise<Registry> {
  const existing = await readRegistry(factory);
  if (existing) return existing;
  const legacy = await probeLegacy();
  const created = nowIso();
  let reg: Registry;
  if (legacy?.account) {
    const server = legacy.account.serverUrl && legacy.account.serverUrl !== location.origin ? legacy.account.serverUrl.replace(/\/+$/, '') : '';
    reg = {
      version: 1,
      activeProfileId: 'acct_legacy',
      profiles: {
        [GUEST_ID]: { id: GUEST_ID, kind: 'guest', dbName: 'autoday-guest', createdAt: created },
        acct_legacy: {
          id: 'acct_legacy',
          kind: 'account',
          dbName: LEGACY_DB,
          createdAt: created,
          server,
          serverOrigin: server || location.origin,
          userId: null,
          email: legacy.account.email,
          name: '',
          accountTag: null,
          rememberMe: true,
          lastVerifiedAt: null,
          legacy: true,
        },
      },
      pendingRevocations: [],
    };
  } else {
    reg = { version: 1, activeProfileId: GUEST_ID, profiles: { [GUEST_ID]: { id: GUEST_ID, kind: 'guest', dbName: LEGACY_DB, createdAt: created } }, pendingRevocations: [] };
  }
  // En annan flik kan ha hunnit skapa registret – den som skrev först vinner.
  const d = await db(factory);
  const tx = d.transaction('kv', 'readwrite');
  const raced = (await tx.store.get(KEY)) as Registry | undefined;
  if (!raced) await tx.store.put(reg, KEY);
  await tx.done;
  return raced ?? reg;
}

// ------------------------------------------------------------------ flikar

/** Meddela andra flikar att aktiv profil ändrats – de laddar om och visar inte längre gamla data. */
export function broadcastProfileChange(activeProfileId: string) {
  try {
    const bc = new BroadcastChannel(CHANNEL);
    bc.postMessage({ type: 'profile-changed', activeProfileId });
    bc.close();
  } catch {
    /* BroadcastChannel saknas – pageshow/fokus-kontrollen fångar ändringen */
  }
}

export function onProfileChange(cb: (activeProfileId: string) => void): () => void {
  try {
    const bc = new BroadcastChannel(CHANNEL);
    bc.onmessage = (e: MessageEvent<{ type?: string; activeProfileId?: string }>) => {
      if (e.data?.type === 'profile-changed' && e.data.activeProfileId) cb(e.data.activeProfileId);
    };
    return () => bc.close();
  } catch {
    return () => undefined;
  }
}
